import {
  createApi as createRtkApi,
  defaultSerializeQueryArgs,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react'
import dayjs from 'dayjs'
import { openSnackbar } from 'store/reducers/snackbar'
import { isTokenValid, parseJWTToken, refreshToken } from 'utils/tokens'

const localEndpoints = process.env.REACT_APP_LOCAL_ENDPOINTS?.split(',') || []
const isRunLocal = (functionName) =>
  localEndpoints.includes(functionName) ? 'http://localhost:9100/' : ''

const createApi = (apiConfig) => {
  // Original endpoints function
  const originalEndpoints = apiConfig.endpoints

  // Modified endpoints function
  const modifiedEndpoints = (builder) => {
    const endpoints = originalEndpoints(builder)
    const modifiedEndpoints = {}

    Object.keys(endpoints).forEach((key) => {
      modifiedEndpoints[key] = {
        ...endpoints[key],
        query: (args) => {
          // Modify the URL here
          const originalQuery = endpoints[key].query(args)

          if (!originalQuery.url) return isRunLocal(key) + originalQuery
          const modifiedUrl = isRunLocal(key) + originalQuery.url
          return { ...originalQuery, url: modifiedUrl }
        },
      }
    })

    return modifiedEndpoints
  }
  return createRtkApi({
    ...apiConfig,
    endpoints: modifiedEndpoints,
  })
}

// Custom baseQuery with retry logic for transient errors
const baseQueryWithRetry = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_MERKLEBASE_API_URL,
    prepareHeaders: async (headers, { endpoint }) => {
      const { accessToken } = JSON.parse(window.localStorage.getItem('serviceToken') || '{}')
      let authorizationToken = accessToken

      if (!accessToken) {
        console.error('No token found in local storage')
      }

      if (!isTokenValid(accessToken)) {
        const isRefreshSuccesfully = await refreshToken(accessToken)

        if (!isRefreshSuccesfully) {
          console.error('Refresh token failed')
          return []
        }

        const { accessToken: refreshedAccessToken } = JSON.parse(
          window.localStorage.getItem('serviceToken') || '{}'
        )
        authorizationToken = refreshedAccessToken
      }

      headers.set('authorization', `${authorizationToken}`)

      if (localEndpoints.includes(endpoint) && authorizationToken)
        headers.set(
          'sls-offline-authorizer-override',
          JSON.stringify({
            lambda: {
              tenantName: 'merklebase',
              userId: parseJWTToken(authorizationToken)?.sub,
            },
          })
        )

      return headers
    },
  })

  // Retry logic for transient errors
  let result = await baseQuery(args, api, extraOptions)
  const maxRetries = 3
  let retryCount = 0

  // Check if we should retry
  const shouldRetry = (result) => {
    // If there's no error, don't retry
    if (!result.error) return false

    const error = result.error
    // Retry on 500 errors (server errors) - these are often transient
    if (error?.status === 500) return true
    // Retry on network errors (connection resets, timeouts, etc.)
    if (error?.status === 'FETCH_ERROR' || error?.status === 'PARSING_ERROR') return true
    // Retry on 502, 503, 504 (gateway/load balancer errors)
    if ([502, 503, 504].includes(error?.status)) return true
    return false
  }

  while (shouldRetry(result) && retryCount < maxRetries) {
    retryCount++
    // Exponential backoff: wait 2^retryCount * 100ms (200ms, 400ms, 800ms), max 2s
    const delay = Math.min(2 ** retryCount * 100, 2000)
    await new Promise((resolve) => setTimeout(resolve, delay))

    console.warn(
      `Retrying request (attempt ${retryCount}/${maxRetries}):`,
      args.url || args,
      result.error
    )
    result = await baseQuery(args, api, extraOptions)
  }

  return result
}

export const merklebaseAPI = createApi({
  reducerPath: 'merklebaseAPI',

  baseQuery: baseQueryWithRetry,
  keepUnusedDataFor: 60, // Keep cached data for 60 seconds
  refetchOnMountOrArgChange: false, // Don't refetch on mount if cached data exists
  tagTypes: [
    'linksData',
    'links',
    'balances',
    'accounts',
    'portfolios',
    'settlementCounterparties',
    'settlementPolicies',
    'settlementRecords',
    'users',
    'roles',
    'views',
    'coins',
    'risk',
    'positions',
    'performanceAnalysis',
    'reports',
    'loans',
    'snapshots',
    'collateralHaircuts',
    'defi',
  ],
  endpoints: (builder) => ({
    // LINKS
    getLinks: builder.query({
      query: () => `links`,
      transformResponse: ({ links }) => links,
      providesTags: ['links'],
    }),
    disconnectLink: builder.mutation({
      query: (id) => ({
        url: `links/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['links', 'linksData'],
    }),
    generateED: builder.mutation({
      query: () => ({
        url: `links/generate/ed25519`,
        method: 'GET',
      }),
    }),
    generateRSACSR: builder.mutation({
      query: () => ({
        url: `links/generate/rsacsr`,
        method: 'GET',
      }),
    }),
    refreshLink: builder.mutation({
      query: (id = '') => ({
        url: `links/refresh/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['links', 'linksData'],
    }),
    connectLink: builder.mutation({
      query: ({ provider, organizationId, name, keys }) => ({
        url: `links`,
        method: 'POST',
        body: {
          name: name,
          keys,
          provider,
          organizationId,
        },
      }),
      invalidatesTags: ['links', 'linksData'],
    }),
    reconnectLink: builder.mutation({
      query: ({ id, keys }) => ({
        url: `links/${id}`,
        method: 'PUT',
        body: {
          keys,
        },
      }),
      invalidatesTags: ['links', 'linksData'],
    }),
    editLink: builder.mutation({
      query: ({ id, name }) => ({
        url: `links/${id}`,
        method: 'PATCH',
        body: {
          name,
        },
      }),
      async onQueryStarted({ id, name }, { dispatch, queryFulfilled, ...x }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getLinks', undefined, (draftLinks) => {
            const updatedLink = draftLinks.find((link) => link.id === id)
            updatedLink.name = name
          })
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: ['links', 'linksData'],
    }),
    pauseLink: builder.mutation({
      query: ({ id, pause }) => ({
        url: `links/pause/${id}`,
        method: 'POST',
        body: { pause },
      }),
      invalidatesTags: ['links', 'linksData'],
    }),
    getPlaidLinkToken: builder.query({
      query: (currency) => `tokens/plaid`,
      transformResponse: ({ userToken }) => userToken,
    }),

    // BALANCES
    getBalances: builder.query({
      query: (params) => ({
        url: `balances`,
        params,
      }),
      transformResponse: (response) => {
        return response.balances.map((balance, index) => ({
          id: index,
          ...balance,
        }))
      },
      providesTags: ['balances', 'linksData'],
    }),
    getAccountsBalances: builder.query({
      query: (params) => ({
        url: `balances/accountsBalances`,
        params,
      }),
      transformResponse: (response) => {
        return response.balances.map((balance, index) => ({
          id: index,
          ...balance,
        }))
      },
      providesTags: ['balances', 'linksData'],
    }),
    getAgreementAllowedCollaterals: builder.query({
      query: (agreementId) => ({
        url: `loans/agreements/allowedcollaterals/${agreementId}`,
      }),
      transformResponse: (response) => {
        return response.allowedCollaterals
      },
      providesTags: ['balances', 'loans', 'coins'],
    }),
    getMarginBalances: builder.query({
      query: (params) => ({
        url: `balances/margin`,
        params,
      }),
      transformResponse: (response) => {
        return response.marginBalances.map((balance, index) => ({
          id: index,
          ...balance,
        }))
      },
      providesTags: ['balances', 'linksData'],
    }),
    getDefiBalances: builder.query({
      query: (params) => ({
        url: `balances/defi`,
        params,
      }),
      transformResponse: ({ balances }) => balances,
      providesTags: ['balances', 'linksData'],
    }),
    getDefiPerformance: builder.query({
      query: (params) => ({
        url: `defi/performance`,
        params,
      }),
      transformResponse: ({ defiPerformance }) => defiPerformance,
      providesTags: ['defi'],
    }),
    getDefiHistoricalPNL: builder.query({
      query: () => ({
        url: `defi/historical-pnl`,
      }),
      transformResponse: ({ data }) => data,
      providesTags: ['defi'],
    }),
    getDefiHistoricalTotalValue: builder.query({
      query: ({ groupAccounts = true } = {}) => ({
        url: `defi/historical-total-value?groupAccounts=${groupAccounts}`,
      }),
      transformResponse: ({ data }) => data,
      providesTags: ['defi'],
    }),
    getComputedBalances: builder.query({
      query: (params) => ({
        url: `balances/computed`,
        params,
      }),
      transformResponse: ({ balances }) => balances,
      providesTags: ['balances', 'positions', 'linksData'],
    }),
    getBalancesByPortfolioByCurrency: builder.query({
      query: (params) => ({
        url: `balances/portfoliobycurrency`,
        params,
      }),
      transformResponse: ({ balances, totalBalances }) => ({
        balances: balances || {},
        totalBalances: totalBalances || {},
      }),
      providesTags: ['balances', 'linksData'],
    }),
    getPositions: builder.query({
      query: (params) => ({
        url: `balances/positions`,
        params: {
          groups: params.groups.join(','),
          pnlBreakdown: params.pnlBreakdown,
          aumByAccount: params.aumByAccount,
          mode: params.mode,
          includeLoans: params.includeLoans,
        },
      }),
      providesTags: ['positions', 'linksData'],
      transformResponse: ({ positions, fundingFees }) => ({
        positions,
        fundingFees,
      }),
    }),
    getDailyPositions: builder.query({
      query: (params) => ({
        url: `balances/positions/daily`,
        params,
      }),
      transformResponse: ({ longShortPositions, strategyPositions }) => ({
        longShortPositions,
        strategyPositions,
      }),
      providesTags: ['positions', 'linksData'],
    }),
    getPositionBreakdown: builder.query({
      query: ({ filters, cursor }) => ({
        url: `balances/positions/breakdown`,
        params: [
          ...Object.entries(filters).map(([key, value]) => value && `${key}=${value}`),
          cursor && `cursor=${cursor}`,
        ]
          .filter(Boolean)
          .join('&'),
      }),
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { cursor, ...newQueryArgs } = queryArgs

        return defaultSerializeQueryArgs({
          endpointName,
          queryArgs: newQueryArgs,
          endpointDefinition,
        })
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.cursor) {
          currentCache.positions.push(...newItems.positions)
          currentCache.cursor = newItems.cursor
        } else {
          currentCache.positions = newItems.positions
          currentCache.cursor = newItems.cursor
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg)
      },
      transformResponse: ({ positions, cursor }) => ({
        positions,
        cursor,
      }),
      providesTags: ['positions', 'linksData'],
    }),

    // DEFI TRANSACTIONS

    getDefiTransactions: builder.query({
      query: (params) => ({
        url: `defi/transactions`,
        params,
      }),
      transformResponse: ({ defiTransactions }) => defiTransactions,
      providesTags: ['defi'],
    }),
    createDefiTransaction: builder.mutation({
      query: (defiTransaction) => ({
        url: `defi/transactions`,
        method: 'POST',
        body: defiTransaction,
      }),
      invalidatesTags: ['defi'],
    }),
    updateDefiTransaction: builder.mutation({
      query: (defiTransaction) => ({
        url: `defi/transactions/${defiTransaction.id}`,
        method: 'PUT',
        body: defiTransaction,
      }),
      invalidatesTags: ['defi'],
    }),
    deleteDefiTransaction: builder.mutation({
      query: (id) => ({
        url: `defi/transactions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['defi'],
    }),

    // SNAPSHOTS
    getSnapshotsSchedules: builder.query({
      query: (params) => ({
        url: `snapshots/schedules`,
        params,
      }),
      providesTags: ['snapshots'],
    }),
    updateSnapshotsSchedules: builder.mutation({
      query: (newSnapshotsSchedules) => ({
        url: `snapshots/schedules`,
        method: 'PUT',
        body: newSnapshotsSchedules,
      }),
      invalidatesTags: ['snapshots'],
    }),
    getAvailableSnapshots: builder.query({
      query: ({ type }) => ({
        url: `snapshots/available${type ? `?type=${type}` : ''}`,
      }),
      providesTags: ['snapshots'],
    }),
    saveSnapshot: builder.mutation({
      query: ({ name, timestamp, type }) => ({
        url: `snapshots`,
        method: 'POST',
        body: { name, timestamp, type },
      }),
      invalidatesTags: ['snapshots'],
    }),
    getSnapshotPositions: builder.query({
      query: ({ snapshotId }) => ({
        url: `snapshots/${snapshotId}/positions`,
      }),
      providesTags: ['snapshots'],
    }),
    getSnapshotBalances: builder.query({
      query: ({ snapshotId }) => ({
        url: `snapshots/${snapshotId}/balances`,
      }),
      providesTags: ['snapshots'],
    }),
    getSnapshotMarginBalances: builder.query({
      query: ({ snapshotId }) => ({
        url: `snapshots/${snapshotId}/marginBalances`,
      }),
      providesTags: ['snapshots'],
    }),
    getSnapshotComputedBalances: builder.query({
      query: ({ snapshotId }) => ({
        url: `snapshots/${snapshotId}/computedBalances`,
      }),
      providesTags: ['snapshots'],
    }),
    getSnapshotDefiBalances: builder.query({
      query: ({ snapshotId }) => ({
        url: `snapshots/${snapshotId}/defiBalances`,
      }),
      providesTags: ['snapshots'],
    }),

    // PRICES
    getPrices: builder.query({
      query: () => `markets/priceschange`,
      transformResponse: (response) => response.currencies,
      providesTags: ['coins'],
    }),
    getHistoryPrices: builder.query({
      query: (params) => ({
        url: `markets/historyprices`,
        params,
      }),
      providesTags: ['coins'],
    }),
    getRTPrices: builder.query({
      query: () => `markets/prices/realtime`,
      transformResponse: (response) => response.prices,
      providesTags: ['coins'],
    }),
    getHPrices: builder.query({
      query: () => `markets/prices/history`,
      transformResponse: (response) => response.prices,
      providesTags: ['coins'],
    }),
    getMissingCurrencies: builder.query({
      query: () => ({
        url: `markets/missingCurrencies`,
      }),
      transformResponse: ({ missingCurrencies }) => missingCurrencies,
      providesTags: ['coins', 'linksData'],
    }),
    createCustomCurrency: builder.mutation({
      query: (currency) => ({
        url: `markets/customCurrency`,
        method: 'POST',
        body: currency,
      }),
      invalidatesTags: ['coins', 'linksData'],
    }),
    updateCustomCurrency: builder.mutation({
      query: (currency) => ({
        url: `markets/customCurrency`,
        method: 'PUT',
        body: currency,
      }),
      invalidatesTags: ['coins', 'linksData'],
    }),
    getInstrumentsPrices: builder.query({
      query: () => `markets/instrumentsprices`,
      transformResponse: (response) => response.instruments,
      providesTags: ['coins'],
    }),
    getInstrumentsHistoryPrices: builder.query({
      query: (params) => ({ url: `markets/instrumentsHistoryPrices`, params }),
      transformResponse: (response) => response.instruments,
      providesTags: ['coins'],
    }),

    // ARC
    getArcProviders: builder.query({
      query: () => ({ url: `markets/arc/providers` }),
      transformResponse: (response) => response.providers,
      providesTags: ['coins'],
    }),
    getArcInstruments: builder.query({
      query: (params) => ({ url: `markets/arc/instruments`, params }),
      transformResponse: (response) => response.instruments,
      providesTags: ['coins'],
    }),

    //FUNDING RATES
    getFundingsRates: builder.query({
      query: () => `markets/fundingRates`,
      transformResponse: (response) => response.fundingRates,
      providesTags: ['fundingFees'],
    }),
    getFundingsRate: builder.query({
      query: ({ instrument, provider }) => ({
        url: `markets/fundingRates/history`,
        params: {
          instrument,
          provider,
        },
      }),
      transformResponse: (response) => response.fundingRate,
      providesTags: ['fundingFees'],
    }),
    getHistoryFundingRates: builder.query({
      query: (params) => ({
        url: `markets/historyFundingRates`,
        params,
      }),
      transformResponse: ({ fundingRates }) => fundingRates,
      providesTags: ['fundingFees'],
    }),
    getCustomFundingRates: builder.query({
      query: () => `markets/customFundingRates`,
      transformResponse: (response) => response.customFundingRates,
      providesTags: ['fundingFees'],
    }),
    addCustomFundingRate: builder.mutation({
      query: (fundingRate) => ({
        url: `markets/customFundingRates`,
        method: 'POST',
        body: fundingRate,
      }),
      invalidatesTags: ['fundingFees'],
    }),
    removeCustomFundingRate: builder.mutation({
      query: (name) => ({
        url: `markets/customFundingRates/${name}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['fundingFees'],
    }),
    updateCustomFundingRate: builder.mutation({
      query: (fundingRate) => ({
        url: `markets/customFundingRates`,
        method: 'PUT',
        body: fundingRate,
      }),
      invalidatesTags: ['fundingFees'],
    }),

    // ACCOUNTS
    getAccounts: builder.query({
      query: (params) => ({ url: `accounts`, params }),
      transformResponse: ({ accounts }) => accounts,
      providesTags: ['accounts', 'linksData'],
    }),
    // PORTFOLIOS
    getPortfolios: builder.query({
      query: ({ ignoreAUM = false }) => `portfolios${ignoreAUM ? '?ignoreAUM' : ''}`,
      transformResponse: ({ portfolios, totalAUM }) => ({ portfolios, totalAUM }),
      providesTags: ['portfolios'],
    }),
    createPortfolio: builder.mutation({
      query: (portfolio) => ({
        url: `portfolios`,
        method: 'POST',
        body: { ...portfolio },
      }),
      invalidatesTags: ['portfolios', 'linksData', 'loans', 'settlementCounterparties'],
    }),
    editPortfolio: builder.mutation({
      query: ({ id, ...portfolio }) => ({
        url: `portfolios/${id}`,
        method: 'PUT',
        body: portfolio,
      }),
      invalidatesTags: ['portfolios', 'linksData', 'loans', 'settlementCounterparties'],
    }),
    removePortfolio: builder.mutation({
      query: (id) => ({
        url: `portfolios/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['portfolios', 'linksData', 'loans'],
    }),
    addAccountsToPortfolio: builder.mutation({
      query: ({ portfolioId, accountsIds }) => ({
        url: `portfolios/${portfolioId}/addAccounts`,
        method: 'POST',
        body: {
          accountsIds,
        },
      }),
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled, ...x }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getPortfolios', undefined, (draftPortfolios) => {
            const portfolioIndex = draftPortfolios.portfolios.findIndex(
              (portfolio) => portfolio.id === patch.portfolioId
            )
            draftPortfolios.portfolios[portfolioIndex].accounts_ids =
              draftPortfolios.portfolios[portfolioIndex].accounts_ids || []
            patch.accountsIds.forEach((id) =>
              draftPortfolios.portfolios[portfolioIndex].accounts_ids.push(id)
            )
          })
        )

        try {
          await queryFulfilled
        } catch {
          dispatch(
            openSnackbar({
              open: true,
              message: 'There was an error while trying add accounts to portfolio',
              variant: 'alert',
              alert: {
                color: 'error',
              },
              close: false,
            })
          )
          patchResult.undo()
        }
      },
      invalidatesTags: ['portfolios', 'linksData', 'loans'],
    }),
    replaceAccountsInPortfolio: builder.mutation({
      query: ({ portfolioId, accountsIds }) => ({
        url: `portfolios/${portfolioId}/replaceAccounts`,
        method: 'POST',
        body: {
          accountsIds,
        },
      }),
      invalidatesTags: ['portfolios', 'linksData', 'loans'],
    }),
    removeAccountsFromPortfolio: builder.mutation({
      query: ({ portfolioId, accountsIds }) => ({
        url: `portfolios/${portfolioId}/removeAccounts`,
        method: 'DELETE',
        body: {
          accountsIds,
        },
      }),
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled, ...x }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getPortfolios', undefined, (draftPortfolios) => {
            const portfolioIndex = draftPortfolios.portfolios.findIndex(
              (portfolio) => portfolio.id === patch.portfolioId
            )
            draftPortfolios.portfolios[portfolioIndex].accounts_ids = draftPortfolios.portfolios[
              portfolioIndex
            ].accounts_ids.filter((id) => !patch.accountsIds.includes(id))
          })
        )

        try {
          await queryFulfilled
        } catch {
          dispatch(
            openSnackbar({
              open: true,
              message: 'There was an error while trying remove accounts from portfolio',
              variant: 'alert',
              alert: {
                color: 'error',
              },
              close: false,
            })
          )
          patchResult.undo()
        }
      },
      invalidatesTags: ['portfolios', 'linksData', 'loans'],
    }),
    replacePortfoliosInPortfolio: builder.mutation({
      query: ({ portfolioId, portfoliosIds }) => ({
        url: `portfolios/${portfolioId}/replacePortfolios`,
        method: 'POST',
        body: {
          portfoliosIds,
        },
      }),
      invalidatesTags: ['portfolios', 'linksData', 'loans'],
    }),
    updateHistoricalAUM: builder.mutation({
      query: ({ portfolioId, aums }) => ({
        url: `portfolios/historicalaum`,
        method: 'PUT',
        body: {
          portfolioId,
          aums,
        },
      }),
      invalidatesTags: ['portfolios', 'linksData'],
    }),
    getHistoricalAUM: builder.query({
      query: ({ portfoliosIds }) => ({
        url: `portfolios/historicalaum`,
        params: {
          portfoliosIds,
        },
      }),
      transformResponse: ({ aums }) => aums,
      providesTags: ['portfolios'],
    }),
    removeHistoricalAUM: builder.mutation({
      query: ({ portfolioId, dates }) => ({
        url: `portfolios/historicalaum`,
        method: 'DELETE',
        body: {
          portfolioId,
          dates,
        },
      }),
      invalidatesTags: ['portfolios', 'linksData'],
    }),
    addPortfolioToPortfolio: builder.mutation({
      query: ({ portfolioId, portfoliosIds }) => ({
        url: `portfolios/${portfolioId}/addPortfolios`,
        method: 'POST',
        body: {
          portfoliosIds,
        },
      }),
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled, ...x }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getPortfolios', undefined, (draftPortfolios) => {
            const portfolioIndex = draftPortfolios.portfolios.findIndex(
              (portfolio) => portfolio.id === patch.portfolioId
            )

            draftPortfolios.portfolios[portfolioIndex].portfolio_ids =
              draftPortfolios.portfolios[portfolioIndex].portfolio_ids || []

            patch.portfoliosIds.forEach((id) =>
              draftPortfolios.portfolios[portfolioIndex].portfolio_ids.push(id)
            )
          })
        )

        try {
          await queryFulfilled
        } catch {
          dispatch(
            openSnackbar({
              open: true,
              message: 'There was an error while trying add accounts to portfolio',
              variant: 'alert',
              alert: {
                color: 'error',
              },
              close: false,
            })
          )
          patchResult.undo()
        }
      },
      invalidatesTags: ['portfolios', 'linksData'],
    }),
    removePortfolioFromPortfolio: builder.mutation({
      query: ({ portfolioId, portfoliosIds }) => ({
        url: `portfolios/${portfolioId}/removePortfolios`,
        method: 'DELETE',
        body: {
          portfoliosIds,
        },
      }),
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled, ...x }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getPortfolios', undefined, (draftPortfolios) => {
            const portfolioIndex = draftPortfolios.portfolios.findIndex(
              (portfolio) => portfolio.id === patch.portfolioId
            )
            draftPortfolios.portfolios[portfolioIndex].portfolio_ids = draftPortfolios.portfolios[
              portfolioIndex
            ].portfolio_ids.filter((id) => !patch.portfoliosIds.includes(id))
          })
        )

        try {
          await queryFulfilled
        } catch {
          dispatch(
            openSnackbar({
              open: true,
              message: 'There was an error while trying remove accounts from portfolio',
              variant: 'alert',
              alert: {
                color: 'error',
              },
              close: false,
            })
          )
          patchResult.undo()
        }
      },
      invalidatesTags: ['portfolios', 'linksData'],
    }),

    // SETTLEMENT COUNTERPARTIES
    getSettlementCounterparties: builder.query({
      query: () => `settlements/counterparties`,
      transformResponse: (response) => {
        if (Array.isArray(response)) return response
        if (response?.data) return response.data
        return []
      },
      providesTags: ['settlementCounterparties'],
    }),
    getSettlementRecords: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams()

        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') return

          if (Array.isArray(value)) {
            value
              .filter(
                (item) =>
                  item !== undefined &&
                  item !== null &&
                  item !== '' &&
                  !(Array.isArray(item) && item.length === 0)
              )
              .forEach((item) => searchParams.append(key, item))
            return
          }

          searchParams.append(key, value)
        })

        const queryString = searchParams.toString()

        return `settlements/records${queryString ? `?${queryString}&window=all` : ''}`
      },
      transformResponse: (response) => {
        if (response?.data || response?.summary || response?.pagination) return response
        return {
          data: Array.isArray(response) ? response : [],
          summary: {},
          pagination: {},
        }
      },
      providesTags: ['settlementRecords'],
    }),
    getSettlementRecordDetails: builder.query({
      query: (id) => `settlements/records/${id}`,
      transformResponse: (response) => {
        if (response?.data) return response.data
        return response
      },
      providesTags: (result, error, id) => [{ type: 'settlementRecords', id }, 'settlementRecords'],
    }),
    createSettlementPolicy: builder.mutation({
      query: (policy) => ({
        url: `settlements/policies`,
        method: 'POST',
        body: policy,
      }),
      invalidatesTags: ['settlementCounterparties', 'settlementPolicies'],
    }),
    getSettlementPolicy: builder.query({
      query: (id) => `settlements/policies/${id}`,
      transformResponse: (response) => {
        if (response?.data) return response.data
        return response
      },
      providesTags: (result, error, id) => [
        { type: 'settlementPolicies', id },
        'settlementPolicies',
      ],
    }),
    updateSettlementPolicy: builder.mutation({
      query: ({ id, ...policy }) => ({
        url: `settlements/policies/${id}`,
        method: 'PUT',
        body: policy,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'settlementPolicies', id },
        'settlementPolicies',
        'settlementCounterparties',
      ],
    }),
    createSettlementPolicyBankAccount: builder.mutation({
      query: ({ policyId, ...payload }) => ({
        url: `settlements/policies/${policyId}/bank-accounts`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { policyId }) => [
        { type: 'settlementPolicies', id: policyId },
        'settlementPolicies',
        'settlementCounterparties',
      ],
    }),
    updateSettlementPolicyBankAccount: builder.mutation({
      query: ({ policyId, accountId, ...payload }) => ({
        url: `settlements/policies/${policyId}/bank-accounts/${accountId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, { policyId }) => [
        { type: 'settlementPolicies', id: policyId },
        'settlementPolicies',
        'settlementCounterparties',
      ],
    }),
    updateSettlementRecord: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `settlements/records/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'settlementRecords', id },
        'settlementRecords',
      ],
    }),
    createSettlementTransaction: builder.mutation({
      query: ({ id, body }) => ({
        url: `settlements/records/${id}/transactions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'settlementRecords', id },
        'settlementRecords',
      ],
    }),
    updateSettlementTransaction: builder.mutation({
      query: ({ id, txId, body }) => ({
        url: `settlements/records/${id}/transactions/${txId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'settlementRecords', id },
        'settlementRecords',
      ],
    }),
    createUploadSettlementDocumentLink: builder.mutation({
      query: ({ id, fileName }) => ({
        url: `settlements/records/${id}/documents/upload-link`,
        method: 'POST',
        body: { fileName },
      }),
      transformResponse: ({ uploadUrl }) => uploadUrl,
    }),
    createDownloadSettlementDocumentLink: builder.mutation({
      query: ({ id, fileName }) => ({
        url: `settlements/records/${id}/documents/download-link`,
        method: 'POST',
        body: { fileName },
      }),
      transformResponse: ({ downloadUrl }) => downloadUrl,
    }),
    unlinkSettlementTransaction: builder.mutation({
      query: ({ id, txId }) => ({
        url: `settlements/records/${id}/transactions/${txId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'settlementRecords', id },
        'settlementRecords',
      ],
    }),
    deleteSettlementPolicyBankAccount: builder.mutation({
      query: ({ policyId, accountId }) => ({
        url: `settlements/policies/${policyId}/bank-accounts/${accountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { policyId }) => [
        { type: 'settlementPolicies', id: policyId },
        'settlementPolicies',
        'settlementCounterparties',
      ],
    }),

    // USERS
    getUser: builder.query({
      query: (id) => {
        return `users/${id}`
      },
      transformResponse: (response) => {
        return response.user
      },
      providesTags: ['users'],
    }),
    getUsers: builder.query({
      query: ({ includeRoles = false, includePortfoliosRoles = false }) => {
        return `users?includeRoles=${includeRoles}&includePortfoliosRoles=${includePortfoliosRoles}`
      },
      transformResponse: (response) => {
        return response.users
      },
      providesTags: ['users'],
    }),
    createUser: builder.mutation({
      query: (user) => ({
        url: `users/create`,
        method: 'POST',
        body: user,
      }),
      invalidatesTags: ['users', 'roles'],
    }),
    updateUser: builder.mutation({
      query: ({ id, ...user }) => ({
        url: `users/${id}`,
        method: 'PUT',
        body: user,
      }),
      invalidatesTags: ['users', 'roles', 'linksData'],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['users', 'roles'],
    }),
    getUserViews: builder.query({
      query: () => {
        return `users/views`
      },
      transformResponse: (response) => {
        return response.views || {}
      },
      providesTags: ['views'],
    }),
    updateUserPortfolios: builder.mutation({
      query: ({ userId, portfolios }) => ({
        url: `users/${userId}/portfolios`,
        method: 'PUT',
        body: {
          portfolios,
        },
      }),
      async onQueryStarted({ userId, portfolios }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getUser', userId, (draftUser) => {
            draftUser.portfolios_ids = portfolios
          })
        )

        try {
          await queryFulfilled
        } catch {
          dispatch(
            openSnackbar({
              open: true,
              message: 'There was an error while trying change portfolios view',
              variant: 'alert',
              alert: {
                color: 'error',
              },
              close: false,
            })
          )
          patchResult.undo()
        }
      },
      invalidatesTags: ['users', 'portfolios', 'linksData', 'loans', 'snapshots', 'defi'],
    }),

    // ROLES
    getRoles: builder.query({
      query: ({
        includePortfolios = false,
        includePermissions = false,
        includeUsers = false,
        includeUserLevelPortfolios = false,
      }) => {
        return `roles?includePortfolios=${includePortfolios}&includePermissions=${includePermissions}&includeUsers=${includeUsers}&includeUserLevelPortfolios=${includeUserLevelPortfolios}`
      },
      transformResponse: (response) => {
        return response.roles
      },
      providesTags: ['roles'],
    }),
    createRole: builder.mutation({
      query: (role) => ({
        url: `roles`,
        method: 'POST',
        body: role,
      }),
      invalidatesTags: ['roles', 'users'],
    }),
    updateRole: builder.mutation({
      query: ({ id, ...role }) => ({
        url: `roles/${id}`,
        method: 'PUT',
        body: role,
      }),
      invalidatesTags: ['roles', 'users'],
    }),
    deleteRole: builder.mutation({
      query: (id) => ({
        url: `roles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['roles', 'users'],
    }),

    // VIEWS — optimistic cache updates (no refetch)
    removeView: builder.mutation({
      query: ({ viewId, type }) => ({
        url: `users/views/${viewId}`,
        method: 'DELETE',
        body: { type },
      }),
      async onQueryStarted({ id, viewId, type }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getUserViews', id, (draft) => {
            if (draft[type]) {
              draft[type] = draft[type].filter((v) => v.id !== viewId)
            }
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
    createView: builder.mutation({
      query: ({ view, type }) => ({
        url: `users/views`,
        method: 'POST',
        body: { view, type },
      }),
      async onQueryStarted({ id, view, type }, { dispatch, queryFulfilled }) {
        // Optimistically add view to cache immediately
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getUserViews', id, (draft) => {
            if (!draft[type]) draft[type] = []
            draft[type].push(view)
          })
        )
        try {
          const { data } = await queryFulfilled
          // Patch in the server-generated ID
          const serverId = data?.view?.id || data?.id
          if (serverId) {
            dispatch(
              merklebaseAPI.util.updateQueryData('getUserViews', id, (draft) => {
                if (draft[type]) {
                  const added = draft[type].find((v) => v.name === view.name && !v.id)
                  if (added) added.id = serverId
                }
              })
            )
          }
        } catch {
          patchResult.undo()
        }
      },
    }),
    updateView: builder.mutation({
      query: ({ view, type }) => ({
        url: `users/views`,
        method: 'PUT',
        body: { type, view },
      }),
      async onQueryStarted({ id, view, type }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          merklebaseAPI.util.updateQueryData('getUserViews', id, (draft) => {
            if (draft[type]) {
              const idx = draft[type].findIndex((v) => v.id === view.id)
              if (idx >= 0) {
                draft[type][idx] = { ...draft[type][idx], ...view }
              }
            }
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    // TRANSACTIONS
    getTransactions: builder.query({
      query: ({ cursor, filter, portfolios }) => {
        let filterQuery = ''
        let cursorQuery = ''
        let portfoliosQuery = ''

        if (filter && Object.keys(filter).length) {
          filterQuery = Object.keys(filter)
            .filter((key) => {
              const value = filter[key]
              if (value === undefined || value === null || value === '') return false
              if (Array.isArray(value)) {
                // Exclude if array is empty or all elements are empty (undefined, null, '', or empty array)
                return value.some(
                  (item) =>
                    item !== undefined &&
                    item !== null &&
                    item !== '' &&
                    !(Array.isArray(item) && item.length === 0)
                )
              }
              return true
            })
            .map((key) => `${key}=${filter[key]}`)
            .join('&')
          if (filterQuery) filterQuery += '&'
        }

        if (cursor) {
          cursorQuery = `cursor=${cursor}&`
        }

        if (portfolios?.length) {
          portfoliosQuery = `portfolios=${portfolios}`
        }

        return `booking/transactions?${cursorQuery}${filterQuery}${portfoliosQuery}`
      },
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { cursor, ...newQueryArgs } = queryArgs

        return defaultSerializeQueryArgs({
          endpointName,
          queryArgs: newQueryArgs,
          endpointDefinition,
        })
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.cursor) {
          currentCache.transactions.push(...newItems.transactions)
          currentCache.cursor = newItems.cursor
        } else {
          currentCache.transactions = newItems.transactions
          currentCache.cursor = newItems.cursor
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg)
      },
      transformResponse: (response) => {
        return { transactions: response.transactions, cursor: response.cursor }
      },
      providesTags: ['positions', 'linksData'],
    }),
    book: builder.mutation({
      query: ({ ids, booked }) => ({
        url: `booking/book`,
        method: 'POST',
        body: { ids, booked },
      }),
      invalidatesTags: ['positions', 'linksData'],
    }),
    createTransaction: builder.mutation({
      query: (transaction) => ({
        url: `booking/transactions/create`,
        method: 'POST',
        body: { transaction },
      }),
      invalidatesTags: ['positions', 'linksData'],
    }),
    editTransaction: builder.mutation({
      query: (transaction) => ({
        url: `booking/transactions/edit`,
        method: 'POST',
        body: { transaction },
      }),
      invalidatesTags: ['positions', 'linksData'],
    }),

    // LIQUIDITY TIERS
    getLiquidityTiers: builder.query({
      query: () => {
        return `balances/liquiditytiers`
      },
      providesTags: ['balances'],
      transformResponse: ({ tiers: liquidityTiers }) => liquidityTiers,
    }),
    createLiquidityTiers: builder.mutation({
      query: (liquidityTiers) => ({
        url: `balances/liquiditytiers`,
        method: 'POST',
        body: { tiers: liquidityTiers },
      }),
      invalidatesTags: ['balances', 'linksData'],
    }),

    // RISK
    getHistoryRisk: builder.query({
      query: (params) => ({
        url: `risk/getHistoryRisk`,
        params,
      }),
      providesTags: ['positions', 'balances', 'linksData'],
    }),
    getCurrencyDistribution: builder.query({
      query: (params) => ({
        url: `risk/getCurrencyDistribution`,
        params,
      }),
      transformResponse: ({ currencyDistribution }) => currencyDistribution,
      providesTags: ['positions', 'linksData'],
    }),
    getStressTests: builder.query({
      query: (params) => ({
        url: `risk/getStressTests`,
        params,
      }),
      transformResponse: ({ stressTests }) => stressTests,
      providesTags: ['positions', 'linksData'],
    }),
    createStressTest: builder.mutation({
      query: (data) => ({
        url: `risk/createStressTest`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['positions', 'linksData'],
    }),
    getRiskHistoryPrices: builder.mutation({
      query: (params) => {
        return {
          url: `risk/getHistoryPrices`,
          method: 'GET',
          params,
          responseHandler: async (response) => {
            var hiddenElement = document.createElement('a')
            var blobCSV = window.URL.createObjectURL(await response.blob())
            hiddenElement.href = blobCSV
            hiddenElement.target = '_blank'
            hiddenElement.download = `Merklebase-historical-prices.xlsx`
            hiddenElement.click()
            return { data: null }
          },
          // window.location.assign(window.URL.createObjectURL(await response.blob())),
          cache: 'no-cache',
        }
      },
    }),
    getCollateralHaircuts: builder.query({
      query: (params) => ({
        url: `loans/collaterals/haircuts`,
        params,
      }),
      transformResponse: ({ haircuts }) => haircuts,
      providesTags: ['collateralHaircuts'],
    }),
    getCollateralHaircut: builder.query({
      query: (id) => ({
        url: `loans/collaterals/haircuts/${id}`,
      }),
      transformResponse: ({ collateralHaircut }) => collateralHaircut,
      providesTags: ['collateralHaircuts'],
    }),
    createCollateralHaircut: builder.mutation({
      query: (data) => ({
        url: `loans/collaterals/haircuts`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['collateralHaircuts'],
    }),
    updateCollateralHaircut: builder.mutation({
      query: ({ id, data }) => ({
        url: `loans/collaterals/haircuts/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['collateralHaircuts'],
    }),
    deleteCollateralHaircut: builder.mutation({
      query: (id) => ({
        url: `loans/collaterals/haircuts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['collateralHaircuts'],
    }),

    // PERFORMANCE
    getPNL: builder.query({
      query: (params) => ({
        url: `performance/pnl`,
        params,
      }),
      providesTags: ['positions', 'linksData'],
    }),
    getPerformanceAnalysis: builder.query({
      query: (params) => ({
        url: `performance/performanceanalysis`,
        params,
      }),
      providesTags: ['performanceAnalysis'],
    }),
    updatePerformanceAnalysis: builder.mutation({
      query: (position) => ({
        url: `performance/performanceanalysis`,
        method: 'PUT',
        body: { position },
      }),
      invalidatesTags: ['performanceAnalysis'],
    }),
    deletePerformanceAnalysis: builder.mutation({
      query: (positionId) => ({
        url: `performance/performanceanalysis`,
        method: 'DELETE',
        body: { positionId },
      }),
      invalidatesTags: ['performanceAnalysis'],
    }),
    getHistoricalPerformance: builder.query({
      query: (params) => ({
        url: `performance/history`,
        params,
      }),
      providesTags: ['positions', 'linksData'],
    }),
    getHistoricalPerformancePositions: builder.query({
      query: (params) => ({
        url: `performance/history/positions`,
        params,
      }),
      providesTags: ['positions', 'linksData'],
    }),
    getHistoricalPerformanceRisk: builder.query({
      query: (params) => ({
        url: `performance/history/risk`,
        params,
      }),
      providesTags: ['positions', 'linksData'],
    }),

    // REPORTS
    getDailyReport: builder.query({
      query: (params) => ({
        url: `reports/daily`,
        method: 'GET',
        params,
      }),
      transformResponse: ({ dailyReports }) => dailyReports,
      providesTags: ['reports', 'linksData'],
    }),
    getReports: builder.query({
      query: (params) => ({
        url: `/reports`,
        method: 'GET',
      }),
      transformResponse: ({ reports }) => reports,
      providesTags: ['reports'],
    }),
    getReport: builder.query({
      query: (reportId) => ({
        url: `/reports/${reportId}`,
        method: 'GET',
      }),
      transformResponse: ({ report }) => report,
      providesTags: ['reports'],
    }),
    createReport: builder.mutation({
      query: (report) => ({
        url: `/reports`,
        method: 'POST',
        body: report,
      }),
      invalidatesTags: ['reports'],
    }),
    editReport: builder.mutation({
      query: (report) => ({
        url: `/reports/${report.id}`,
        method: 'PUT',
        body: report,
      }),
      invalidatesTags: ['reports'],
    }),
    deleteReport: builder.mutation({
      query: (reportId) => ({
        url: `/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['reports'],
    }),
    generateReport: builder.mutation({
      query: (reportId, startDate, endDate) => ({
        url: `/reports/${reportId}/generate?startDate=${dayjs(startDate).format('YYYY-MM-DD')}${
          endDate ? `&endDate=${dayjs(endDate).format('YYYY-MM-DD')}` : ''
        }`,
        method: 'GET',
        cache: 'no-cache',
        responseHandler: async (response) => {
          return await response.blob()
        },
      }),
      invalidatesTags: ['reports'],
    }),

    //LOANS
    getAgreements: builder.query({
      query: (params) => ({
        url: `loans/agreements`,
        params,
      }),
      providesTags: ['loans'],
    }),
    getAgreementPaymentsSchedule: builder.query({
      query: ({ agreementId }) => ({
        url: `/loans/agreements/${agreementId}/payments`,
      }),
      providesTags: ['loans'],
    }),
    createAgreement: builder.mutation({
      query: (agreement) => ({
        url: `loans/agreements`,
        method: 'POST',
        body: agreement,
      }),
      invalidatesTags: ['loans'],
    }),
    updateAgreement: builder.mutation({
      query: (agreement) => ({
        url: `loans/agreements/${agreement.id}`,
        method: 'PUT',
        body: agreement,
      }),
      invalidatesTags: ['loans'],
    }),
    deleteAgreement: builder.mutation({
      query: (agreementId) => ({
        url: `loans/agreements/${agreementId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['loans'],
    }),
    createUploadAgreementFileLink: builder.mutation({
      query: ({ agreementId, fileName }) => ({
        url: `loans/agreements/${agreementId}/upload`,
        method: 'POST',
        body: {
          fileName,
        },
      }),
      transformResponse: ({ uploadLink }) => uploadLink,
    }),
    createDownloadAgreementFileLink: builder.mutation({
      query: ({ agreementId, fileName }) => ({
        url: `loans/agreements/${agreementId}/download`,
        method: 'POST',
        body: {
          fileName,
        },
      }),
      transformResponse: ({ downloadLink }) => downloadLink,
    }),
    getTranches: builder.query({
      query: (params) => ({
        url: `loans/tranches`,
        params,
      }),
      providesTags: ['loans'],
    }),
    getTranchePaymentsSchedule: builder.query({
      query: ({ trancheId }) => ({
        url: `/loans/tranches/${trancheId}/payments`,
      }),
      providesTags: ['loans'],
    }),
    createTranche: builder.mutation({
      query: (tranche) => ({
        url: `loans/tranches`,
        method: 'POST',
        body: tranche,
      }),
      invalidatesTags: ['loans', 'balances'],
    }),
    updateTranche: builder.mutation({
      query: (tranche) => ({
        url: `loans/tranches/${tranche.id}`,
        method: 'PUT',
        body: tranche,
      }),
      invalidatesTags: ['loans', 'balances'],
    }),
    deleteTranche: builder.mutation({
      query: (trancheId) => ({
        url: `loans/tranches/${trancheId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['loans'],
    }),
    createUploadTrancheFileLink: builder.mutation({
      query: ({ trancheId, fileName }) => ({
        url: `loans/tranches/${trancheId}/upload`,
        method: 'POST',
        body: {
          fileName,
        },
      }),
      transformResponse: ({ uploadLink }) => uploadLink,
    }),
    createDownloadTrancheFileLink: builder.mutation({
      query: ({ trancheId, fileName }) => ({
        url: `loans/tranches/${trancheId}/download`,
        method: 'POST',
        body: {
          fileName,
        },
      }),
      transformResponse: ({ downloadLink }) => downloadLink,
    }),
    createPayment: builder.mutation({
      query: (payment) => ({
        url: `loans/payments`,
        method: 'POST',
        body: payment,
      }),
      invalidatesTags: ['loans'],
    }),
    updatePayment: builder.mutation({
      query: (payment) => ({
        url: `loans/payments/${payment.id}`,
        method: 'PUT',
        body: payment,
      }),
      invalidatesTags: ['loans'],
    }),
    calculateAccruals: builder.mutation({
      query: (params) => ({
        url: `loans/accruals/calculate`,
        method: 'POST',
        body: params,
      }),
    }),

    // EVENTS
    getEvents: builder.query({
      query: (params) => ({
        url: `events`,
        params,
      }),
      providesTags: ['events'],
    }),
    getUserEnabledEventTypes: builder.query({
      query: () => ({
        url: `events/config`,
      }),
      providesTags: ['events'],
    }),
    updateUserEnabledEventTypes: builder.mutation({
      query: ({ eventTypes }) => ({
        url: `events/config`,
        method: 'POST',
        body: {
          eventTypes,
        },
      }),
      invalidatesTags: ['events'],
    }),

    // ALERTS
    getUserAlerts: builder.query({
      query: (params) => ({
        url: `alerts`,
        params,
      }),
      providesTags: ['alerts'],
    }),
    updateUserAlert: builder.mutation({
      query: ({ alertId, alert }) => ({
        url: `alerts/${alertId}`,
        method: 'PUT',
        body: alert,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Only invalidate tags if the request was successful
          dispatch(merklebaseAPI.util.invalidateTags(['alerts']))
        } catch {
          // Do nothing on failure - don't invalidate tags
        }
      },
    }),
    insertUserAlert: builder.mutation({
      query: (alert) => ({
        url: `alerts`,
        method: 'POST',
        body: alert,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Only invalidate tags if the request was successful
          dispatch(merklebaseAPI.util.invalidateTags(['alerts']))
        } catch {
          // Do nothing on failure - don't invalidate tags
        }
      },
    }),
    deleteUserAlert: builder.mutation({
      query: (alertId) => ({
        url: `alerts/${alertId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          // Only invalidate tags if the request was successful
          dispatch(merklebaseAPI.util.invalidateTags(['alerts']))
        } catch {
          // Do nothing on failure - don't invalidate tags
        }
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // LINKS
  useGetLinksQuery,
  useDisconnectLinkMutation,
  useRefreshLinkMutation,
  useConnectLinkMutation,
  useGenerateEDMutation,
  useGenerateRSACSRMutation,
  useReconnectLinkMutation,
  useEditLinkMutation,
  useGetPlaidLinkTokenQuery,
  usePauseLinkMutation,

  // BALANCES
  useGetBalancesQuery,
  useGetAccountsBalancesQuery,
  useGetAgreementAllowedCollateralsQuery,
  useGetComputedBalancesQuery,
  useGetPositionsQuery,
  useLazyGetPositionsQuery,
  useGetPositionBreakdownQuery,
  useLazyGetPositionBreakdownQuery,
  useGetLiquidityTiersQuery,
  useCreateLiquidityTiersMutation,
  useGetDailyPositionsQuery,
  useGetBalancesByPortfolioByCurrencyQuery,
  useGetMarginBalancesQuery,
  useGetDefiBalancesQuery,
  useGetDefiPerformanceQuery,
  useGetDefiHistoricalPNLQuery,
  useGetDefiHistoricalTotalValueQuery,

  // SETTLEMENTS
  useGetSettlementCounterpartiesQuery,
  useGetSettlementRecordsQuery,
  useGetSettlementRecordDetailsQuery,
  useCreateSettlementPolicyMutation,
  useGetSettlementPolicyQuery,
  useUpdateSettlementPolicyMutation,
  useCreateSettlementPolicyBankAccountMutation,
  useUpdateSettlementPolicyBankAccountMutation,
  useDeleteSettlementPolicyBankAccountMutation,
  useUpdateSettlementRecordMutation,
  useCreateSettlementTransactionMutation,
  useUpdateSettlementTransactionMutation,
  useUnlinkSettlementTransactionMutation,
  useCreateUploadSettlementDocumentLinkMutation,
  useCreateDownloadSettlementDocumentLinkMutation,

  // DEFI TRANSACTIONS
  useGetDefiTransactionsQuery,
  useCreateDefiTransactionMutation,
  useUpdateDefiTransactionMutation,
  useDeleteDefiTransactionMutation,

  // SNAPSHOTS
  useGetSnapshotsSchedulesQuery,
  useUpdateSnapshotsSchedulesMutation,
  useGetSnapshotPositionsQuery,
  useGetAvailableSnapshotsQuery,
  useSaveSnapshotMutation,
  useGetSnapshotBalancesQuery,
  useGetSnapshotMarginBalancesQuery,
  useGetSnapshotComputedBalancesQuery,
  useGetSnapshotDefiBalancesQuery,

  // PRICES
  useGetPricesQuery,
  useGetHistoryPricesQuery,
  useGetMissingCurrenciesQuery,
  useCreateCustomCurrencyMutation,
  useUpdateCustomCurrencyMutation,
  useGetInstrumentsPricesQuery,
  useGetInstrumentsHistoryPricesQuery,
  useGetRTPricesQuery,
  useGetHPricesQuery,

  // ARC
  useGetArcProvidersQuery,
  useGetArcInstrumentsQuery,

  // FUNDING RATES
  useGetFundingsRatesQuery,
  useGetFundingsRateQuery,
  useGetHistoryFundingRatesQuery,
  useGetCustomFundingRatesQuery,
  useAddCustomFundingRateMutation,
  useRemoveCustomFundingRateMutation,
  useUpdateCustomFundingRateMutation,

  // USERS
  useGetUserQuery,
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserViewsQuery,
  useUpdateUserPortfoliosMutation,

  // ROLES
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,

  // ACCOUNTS
  useGetAccountsQuery,

  // PORTFOLIOS
  useGetPortfoliosQuery,
  useEditPortfolioMutation,
  useCreatePortfolioMutation,
  useRemovePortfolioMutation,
  useAddAccountsToPortfolioMutation,
  useReplaceAccountsInPortfolioMutation,
  useRemoveAccountsFromPortfolioMutation,
  useAddPortfolioToPortfolioMutation,
  useReplacePortfoliosInPortfolioMutation,
  useRemovePortfolioFromPortfolioMutation,
  useGetHistoricalAUMQuery,
  useUpdateHistoricalAUMMutation,
  useRemoveHistoricalAUMMutation,

  // TRANSACTIONS
  useGetTransactionsQuery,
  useBookMutation,
  useCreateTransactionMutation,
  useEditTransactionMutation,

  // VIEWS
  useRemoveViewMutation,
  useCreateViewMutation,
  useUpdateViewMutation,

  // RISK
  useGetHistoryRiskQuery,
  useGetRiskHistoryPricesMutation,
  useGetCurrencyDistributionQuery,
  useGetCollateralHaircutsQuery,
  useGetCollateralHaircutQuery,
  useCreateCollateralHaircutMutation,
  useUpdateCollateralHaircutMutation,
  useDeleteCollateralHaircutMutation,

  // PERFORMANCE
  useGetPNLQuery,
  useLazyGetPNLQuery,
  useGetHistoricalPerformanceQuery,
  useGetHistoricalPerformancePositionsQuery,
  useGetHistoricalPerformanceRiskQuery,
  useGetPerformanceAnalysisQuery,
  useUpdatePerformanceAnalysisMutation,
  useDeletePerformanceAnalysisMutation,
  useGetStressTestsQuery,
  useCreateStressTestMutation,

  // REPORTS
  useGetDailyReportQuery,
  useGetReportsQuery,
  useGetReportQuery,
  useCreateReportMutation,
  useEditReportMutation,
  useDeleteReportMutation,
  useGenerateReportMutation,

  // LOANS
  useGetAgreementsQuery,
  useGetAgreementPaymentsScheduleQuery,
  useCreateAgreementMutation,
  useUpdateAgreementMutation,
  useDeleteAgreementMutation,
  useCreateUploadAgreementFileLinkMutation,
  useCreateDownloadAgreementFileLinkMutation,
  useGetTranchesQuery,
  useGetTranchePaymentsScheduleQuery,
  useLazyGetTranchesQuery,
  useCreateTrancheMutation,
  useUpdateTrancheMutation,
  useDeleteTrancheMutation,
  useCreateUploadTrancheFileLinkMutation,
  useCreateDownloadTrancheFileLinkMutation,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useCalculateAccrualsMutation,

  // EVENTS
  useGetEventsQuery,
  useGetUserEnabledEventTypesQuery,
  useUpdateUserEnabledEventTypesMutation,

  // ALERTS
  useGetUserAlertsQuery,
  useUpdateUserAlertMutation,
  useInsertUserAlertMutation,
  useDeleteUserAlertMutation,
} = merklebaseAPI
